import ReactGA from 'react-ga4'

const enabled = process.env.NODE_ENV !== "development"

/*
 * send_page_view:false because this app sends its own. gtag's `config` fires a page_view on
 * initialise, and components/Analytics.js fires one for the same landing screen a moment later --
 * so the first page of every session was counted twice. Route changes are ours to report: GA
 * cannot see a react-router navigation unless "page changes based on browser history events" is
 * enabled in Enhanced measurement, which would then double-count every navigation instead.
 */
export const initializeGA = (gtag) => {
	enabled && ReactGA.initialize(gtag, {gtagOptions: {send_page_view: false}})
}

export const setUserId = (userId) => {
	enabled && ReactGA.set({
		user_id: userId || null,         // Null for logged-out users
		is_logged_in: !!userId          // Boolean indicating logged-in state
	})
}

export const trackLogin = () => {
	// `login` is a GA4 recommended event name, so it is reported on without configuration
	enabled && ReactGA.event('login', {method: 'supabase'})
}

/*
 * Pins page_location to a URL with NO fragment, then sends the page view.
 *
 * gtag stamps page_location onto every event from document.location.href, and href includes the
 * `#...` fragment. /auth/callback receives a Supabase magic link as #access_token=<session token>,
 * so any event firing on that screen would hand Google a live credential. GA's Redact data feature
 * does not catch it -- that looks for email-shaped text, not tokens.
 *
 * Setting it per navigation covers every event that follows, not just this one.
 */
export const trackPageView = (path) => {
	if (!enabled) return

	ReactGA.set({ page_location: `${window.location.origin}${path}` })
	ReactGA.send({ hitType: 'pageview', page: path })
}

/*
 * GA4 caps a parameter value at 100 characters and silently drops anything longer, so values are
 * trimmed here rather than discovering later that a whole event never arrived.
 */
const clip = (value, max = 100) => {
	const text = String(value == null ? '' : value).replace(/\s+/g, ' ').trim()
	return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/*
 * The generic sender. Replaces mixpanel.track for the handful of events written by hand (094).
 */
export const trackEvent = (name, params = {}) => {
	if (!enabled) return

	const clean = {}
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null) continue
		clean[key] = typeof value === 'number' || typeof value === 'boolean' ? value : clip(value)
	}
	ReactGA.event(name, clean)
}

// what counts as "someone clicked a thing" rather than "someone clicked the page"
const INTERACTIVE = 'button, a, [role="button"], [role="link"], [role="tab"], [role="menuitem"], .MuiButtonBase-root, [data-ga]'

/*
 * Blanket click tracking (094), replacing what Mixpanel's autocapture was doing.
 *
 * One delegated listener in the CAPTURE phase, so it still sees clicks whose handlers stop
 * propagation -- which most MUI controls do. It reports the nearest interactive ancestor of
 * whatever was clicked, and ignores clicks that land on nothing interactive, so page background
 * clicks do not flood the property.
 *
 * It never reads input values. The label comes from the control itself -- a data-ga attribute, an
 * aria-label, or the control's own text -- never from surrounding content and never from what
 * anyone typed. Session recording is deliberately not reproduced.
 */
export const initAutoTracking = () => {
	if (!enabled || typeof document === 'undefined') return () => {}

	const onClick = (event) => {
		const target = event.target && event.target.closest && event.target.closest(INTERACTIVE)
		if (!target) return

		// a text input inside a clickable wrapper is still typing, not a button press
		const tag = target.tagName.toLowerCase()
		if (tag === 'input' && ['text', 'email', 'password', 'search', 'tel', 'url'].includes(target.type)) return

		/*
		 * `data-ga` is looked for on ANY ancestor, not just the interactive element itself, so a
		 * marked-up card still reports its own name when the click lands on a button inside it.
		 *
		 * When it is present the element's text is never read. That is the whole point: a control
		 * whose text is content -- a topic name, an answer, a product title -- must report WHAT KIND
		 * of thing was clicked, not what it said. Text is only used for controls nobody marked up,
		 * which are the fixed ones: Save, Continue, Back.
		 */
		const marked = target.closest('[data-ga]')
		const label = marked
			? marked.getAttribute('data-ga')
			: (target.getAttribute('aria-label') || target.innerText || target.getAttribute('title') || '')

		trackEvent('ui_click', {
			label: label,
			/*
			 * A fixed-domain detail worth keeping: an option LETTER is one of four, a tab index is
			 * one of a handful. Never a name, a title or anything a user or an author wrote --
			 * those are exactly the high-cardinality values this design exists to keep out.
			 */
			value: marked ? marked.getAttribute('data-ga-value') || undefined : undefined,
			element: target.getAttribute('type') ? `${tag}[${target.getAttribute('type')}]` : tag,
			href: tag === 'a' ? target.getAttribute('href') : undefined,
			page_path: window.location.pathname,
		})
	}

	document.addEventListener('click', onClick, true)
	return () => document.removeEventListener('click', onClick, true)
}

export const trackViewItem = (selectedPackage, selectedOption) => {
	enabled && ReactGA.event('view_item', {
		items: [
			{
				item_id: selectedPackage.id,
				item_name: selectedPackage.name && (selectedOption && (' - ' + selectedOption.name)),
				item_category: selectedPackage.category ? selectedPackage.category.name : 'null'
			}
		]
	})
}

export const trackBeginCheckout = (stripeId, selectedPackage, selectedOption) => {
	if(enabled) {
		const price = selectedOption.price / 100
		ReactGA.event('begin_checkout', {
			transaction_id: stripeId,
			currency: 'USD',
			value: price,
			items: [
				{
					item_id: selectedPackage.id, // Focus on the package ID
					item_name: selectedPackage.name, // Focus on the package name
					item_variant: selectedOption.name, // Include the option as a secondary detail
					item_category: selectedPackage.category ? selectedPackage.category.name : 'null',
					price: price,
					quantity: 1
				}
			]
		})
	}
}

export const trackPurchase = (paymentIntentId, selectedPackage, selectedOption) => {
	if(enabled) {
		const price = selectedOption.price_in_cents / 100
		ReactGA.event('purchase', {
			transaction_id: paymentIntentId,
			value: price,
			currency: 'USD',
			items: [{
				item_id: selectedPackage.id, // Focus on the package ID
				item_name: selectedPackage.name, // Focus on the package name
				item_variant: selectedOption.name, // Include the option as a secondary detail
				item_category: selectedPackage.category ? selectedPackage.category.name : 'null',
				price: price,
				quantity: 1
			}]
		})
	}
}

export const trackPurchaseFromLicense = (license, categoryName) => {
	if(enabled) {
		const stripeId = license.checkout_session_id || license.payment_intent_id
		const price = license.package_price_in_cents / 100
		ReactGA.event('purchase', {
			transaction_id: stripeId,
			value: price,
			currency: 'USD',
			items: [{
				item_id: license.package_id, // Focus on the package ID
				item_name: license.package_name, // Focus on the package name
				item_variant: license.package_option_name, // Include the option as a secondary detail
				item_category: categoryName || 'null',
				price: price,
				quantity: 1
			}]
		})
	}
}
